import React, { useRef } from 'react';

import { AlineTransforms } from '@aline/common';
import { link } from '@aline/routes';
import clsx from 'clsx';
import { FilePenLine, MessageSquareText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Range } from 'slate';
import { AlineTooltip } from 'src/components';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { useDocumentParams } from 'src/helpers';
import { useGetDocumentComments } from 'src/hooks';
import { useEditor } from 'src/layout-engine/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import type { GroupedAnnotationMarker } from './PageAnnotationMarker.helpers';
import { getNextEditRange, getSelectedMarkerCommentId } from './PageAnnotationMarker.helpers';
import { ReactEditor } from '../react-editor';

export { groupAnnotationMarkers } from './PageAnnotationMarker.helpers';
export type { GroupedAnnotationMarker } from './PageAnnotationMarker.helpers';

const ICON_SIZE_PX = 20;
const ICON_WRAPPER_PX = 28;
interface StyleProps {
  hasMultipleTargets: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { hasMultipleTargets }) => ({
  iconButton: {
    '& svg': {
      display: 'block',
      flexShrink: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flex: `0 0 ${ICON_WRAPPER_PX}px`,
    height: ICON_WRAPPER_PX,
    justifyContent: 'center',
    width: ICON_WRAPPER_PX,
  },
  iconButtonDivider: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  wrapper: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: ICON_WRAPPER_PX,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    transform: hasMultipleTargets ? 'translateX(75%)' : 'translateX(50%)',
    zIndex: 1,
  },
}));

export function PageAnnotationMarker({
  commentIds,
  commentRanges,
  editItems,
  y,
}: GroupedAnnotationMarker): JSX.Element | null {
  const editor = useEditor();
  const navigate = useNavigate();
  const hasEdits = editItems.length > 0;
  const hasComments = commentIds.size > 0;
  const hasMultipleTargets = hasComments && hasEdits;
  const { classes } = useStyles({ hasMultipleTargets });
  const { documentId } = useDocumentParams();
  const { commentsById } = useGetDocumentComments(documentId);
  const { openDrawer } = useGlobalDrawer();
  const commentIndexRef = useRef(0);
  const { comments, trackedChanges } = useEditorTabStateActions();

  function selectRange(range: Range) {
    AlineTransforms.select(editor, range);
    ReactEditor.blur(editor);
  }

  function handleClickComment() {
    const commentRange = getNextIndexedItem(commentRanges, commentIndexRef);
    if (commentRange) {
      selectRange(commentRange);
      const commentId = getSelectedMarkerCommentId(editor, commentsById);
      if (commentId) comments.requestScrollToComment(commentId);
    }
    navigate(link.DocumentDetailComments({ documentId }), { replace: true });
    openDrawer();
  }

  function handleClickSuggestedEdit() {
    const editRange = getNextEditRange(editor.selection, editItems);
    if (editRange) {
      selectRange(editRange);
      trackedChanges.requestScrollToChange(editRange);
    }
    navigate(link.DocumentDetailTrackedChanges({ documentId }), { replace: true });
    openDrawer();
  }

  if (!hasComments && !hasEdits) return null;

  return (
    <div
      className={classes.wrapper}
      style={{ top: y }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseMove={stopPropagation}
      onMouseUp={stopPropagation}
    >
      {hasComments && (
        <AlineTooltip
          arrow
          placement="top"
          title={commentIds.size === 1 ? '1 comment' : `${commentIds.size} comments`}
        >
          <div className={classes.iconButton} onClick={handleClickComment}>
            <MessageSquareText size={ICON_SIZE_PX - 4} strokeWidth={1.5} />
          </div>
        </AlineTooltip>
      )}

      {hasEdits && (
        <AlineTooltip
          arrow
          placement="top"
          title={
            editItems.length === 1 ? '1 suggested edit' : `${editItems.length} suggested edits`
          }
        >
          <div
            className={clsx(classes.iconButton, hasComments && classes.iconButtonDivider)}
            onClick={handleClickSuggestedEdit}
          >
            <FilePenLine size={ICON_SIZE_PX - 4} strokeWidth={1.5} />
          </div>
        </AlineTooltip>
      )}
    </div>
  );
}

function getNextIndexedItem<T>(items: T[], indexRef: React.MutableRefObject<number>) {
  if (!items.length) return null;

  const item = items[indexRef.current % items.length];
  indexRef.current = (indexRef.current + 1) % items.length;
  return item;
}

function stopPropagation(event: React.MouseEvent) {
  event.stopPropagation();
}
